import { Alert, Modal } from 'react-bootstrap';
import { useState, type VoidFunctionComponent } from 'react';
import qs from 'qs';
import type Stripe from 'stripe-latest';

import { BUTTON_SIZE, BUTTON_TYPE, LoaderButton } from '../../../generic/Button';
import { formatPrice, generateSteps } from '../utils';
import { usePlansContext } from './PlansContext';
import { useUpdatePlan } from '../../../../hooks/subscriptions/useUpdatePlan';
import { window } from '../../../../globals';
import FeaturesComponent from '../Features';
import LimitsComponent from '../Limits';
import RangeSlider from '../../../generic/RangeSlider';
import styles from './styles.module.less';
import { isOrgInFreeTier } from '../../../../helpers/subscriptions';
import Loader from '../../../generic/Loader';
import { useSynchronizeSubscription } from '../../../../hooks/subscriptions/useSynchronizeSubscription';
import { useOnMount } from '../../../../hooks';
import { useAddToast } from '../../../../contexts/ToastContext';
import { LONG_DATE_FORMAT } from '../../../../helpers/date';
import { useRouter } from '../../../../contexts/RouterContext';

type GetButtonParams = {
  hasUsedFreeTrial?: boolean;
  isSelectedCurrent: boolean;
  trialDaysRemaining?: number;
};

function getButtonText({
  hasUsedFreeTrial,
  isSelectedCurrent,
  trialDaysRemaining,
}: GetButtonParams): string {
  if (trialDaysRemaining && isSelectedCurrent) {
    return `Trial in Progress - ${trialDaysRemaining} days remaining`;
  }

  if (isSelectedCurrent) {
    return 'Current Plan';
  }

  if (isOrgInFreeTier()) {
    if (!hasUsedFreeTrial) {
      return 'Start 14-day Business XL Trial Today';
    }

    return 'Upgrade plan';
  }

  return 'Change Plan';
}

function hasUpdatedPlanParam(): boolean {
  const params = qs.parse(window.location.search.substring(1));
  return !!params?.update_success;
}

type MessageModalProps = {
  onHide: () => void;
};

const MessageModal: VoidFunctionComponent<MessageModalProps> = ({
  onHide,
}: MessageModalProps) => {
  const {
    isLoading,
    mutate: synchronizeSubscription,
  } = useSynchronizeSubscription();

  const {
    currentPlan,
    getPlanName,
    isLoading: isLoadingCurrentPlan,
  } = usePlansContext();

  const router = useRouter();

  useOnMount(synchronizeSubscription);

  const handleHide = () => {
    router.replace({ pathname: window.location.pathname, search: '?tab=Plans' });
    onHide();
  };

  let text = `You are now subscribed to plan '${getPlanName(currentPlan?.plan)}'.`;

  const currentPlanEndDate = currentPlan?.endDate;
  if (currentPlan?.nextScheduledPriceId && currentPlanEndDate) {
    const date = new Date(currentPlanEndDate * 1000);
    text = `Your current plan '${getPlanName(currentPlan?.plan)}' is scheduled to end on ${LONG_DATE_FORMAT.format(date)}.`;
  }

  return (
    <Modal show onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {
            isLoading || isLoadingCurrentPlan
              ? 'Updating...'
              : 'Your plan was updated!'
          }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {
          isLoading || isLoadingCurrentPlan
            ? <Loader />
            : <p>{text}</p>
        }
      </Modal.Body>
    </Modal>
  );
};

/**
 * Renders the price component specifically for the BUSINESS plan.
 * Displays a list of available prices, allowing users to choose a price level and view relevant
 * features and usage limits for the selected tier.
 */
const DisplayBusinessPlan: VoidFunctionComponent = () => {
  const {
    currentPlan,
    getPlanName,
    selectedBusinessPrice: selectedPrice,
    viewInterval,
  } = usePlansContext();

  const {
    data: updateResult,
    isLoading: isUpdating,
    mutateAsync: updatePlan,
    isSuccess: hasUpdatedPlan,
  } = useUpdatePlan();

  const [
    shouldShowModal,
    setShouldShowModal,
  ] = useState<boolean>(hasUpdatedPlanParam() || hasUpdatedPlan);

  const addToast = useAddToast();

  if (!selectedPrice || !currentPlan) {
    return null;
  }

  const isSelectedCurrent = currentPlan.plan === selectedPrice.id;

  if (updateResult?.sessionUrl) {
    // Redirects to Stripe checkout if there's a session URL
    window.location = updateResult.sessionUrl;
  }

  const currentPlanEndDate = currentPlan?.endDate;
  let endDate;
  let nextPlanName;
  if (currentPlan?.nextScheduledPriceId && currentPlanEndDate) {
    endDate = new Date(currentPlanEndDate * 1000);
    nextPlanName = getPlanName(currentPlan.nextScheduledPriceId);
  }

  const onClick = () => {
    updatePlan(selectedPrice.id)
      .then((u) => {
        if (u?.sessionUrl) {
          // User is going to checkout, do not show modal
          return u;
        }
        setShouldShowModal(true);
        return u;
      })
      .catch((e) => {
        addToast({ type: 'error', content: `An unexpected error happened. Error message: ${e.message}` });
      });
  };

  return (
    <>
      {
        shouldShowModal &&
        <MessageModal
          onHide={() => setShouldShowModal(false)}
        />
      }
      <DisplayDiscount />
      <span className={styles.price}>{formatPrice(selectedPrice.unit_amount || 0, viewInterval)}</span>
      <DisplayBusinessPricesSlider />
      <LoaderButton
        size={BUTTON_SIZE.LARGE}
        type={isSelectedCurrent ? BUTTON_TYPE.SECONDARY : BUTTON_TYPE.PRIMARY}
        isFullWidth
        onClick={onClick}
        isDisabled={isSelectedCurrent}
        isLoading={isUpdating}
      >
        { getButtonText({
          hasUsedFreeTrial: currentPlan.hasUsedFreeTrial,
          isSelectedCurrent,
          trialDaysRemaining: currentPlan.trialDaysRemaining,
        }) }
      </LoaderButton>
      {
        endDate &&
        <Alert type="error">
          {
            `This plan is scheduled to end on ${LONG_DATE_FORMAT.format(endDate)}.
            After that you'll be on the plan '${nextPlanName}'.`
          }
        </Alert>
      }
      <LimitsComponent price={selectedPrice} />
      <FeaturesComponent product={selectedPrice.product as Stripe.Product} />
    </>
  );
};

export const DisplayBusinessPricesSlider: VoidFunctionComponent = () => {
  const {
    selectedBusinessPrice: selectedPrice,
    filteredBusinessPrices: filteredPrices,
    handleBusinessPriceChange: handlePriceChange,
  } = usePlansContext();

  if (!selectedPrice) {
    return null;
  }
  return (
    <div className={styles.priceSliderContainer}>
      <RangeSlider
        steps={generateSteps(filteredPrices)}
        onChange={handlePriceChange}
        defaultStepValue={selectedPrice.id}
      />
    </div>
  );
};

const DisplayDiscount: VoidFunctionComponent = () => {
  const {
    businessDiscount: discount,
  } = usePlansContext();

  if (!discount) {
    return null;
  }
  return (
    <div className={styles.discount}>
      {discount}% discount
    </div>
  );
};

export default DisplayBusinessPlan;
